// components/ExcelPasteModal.jsx
import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  ContentPaste as PasteIcon,
  Description as ExcelIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useExcelPaste } from '../hooks/useExcelPaste';
import DataPreviewTable from './DataPreviewTable';

const steps = ['Paste Data', 'Preview Data', 'Konfirmasi'];

function ExcelPasteModal({ open, onClose, onDataImported }) {
  const {
    data,
    headers,
    rawData,
    parseExcelClipboardData,
    parseExcelFile,
    exportToExcel,
    clearData,
    hasData,
    summary
  } = useExcelPaste();

  const [activeStep, setActiveStep] = useState(0);
  const [pasteText, setPasteText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handlePasteTextChange = (e) => {
    const text = e.target.value;
    setPasteText(text);
    
    if (text.trim()) {
      const result = parseExcelClipboardData(text);
      if (result.success) {
        setError('');
      } else {
        setError(result.message);
      }
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    setError('');

    try {
      const result = await parseExcelFile(file);
      if (result.success) {
        setError('');
        if (activeStep === 0) {
          setActiveStep(1);
        }
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError(`Gagal memproses file: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerFileUpload = () => {
    fileInputRef.current.click();
  };

  const handleNext = () => {
    if (activeStep === 0 && hasData) {
      setActiveStep(1);
    } else if (activeStep === 1) {
      setActiveStep(2);
    } else if (activeStep === 2) {
      handleSubmit();
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = () => {
    if (onDataImported && hasData) {
      onDataImported(data, headers, summary);
    }
    handleReset();
    onClose();
  };

  const handleReset = () => {
    clearData();
    setPasteText('');
    setActiveStep(0);
    setError('');
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleExport = () => {
    exportToExcel(`data-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" paragraph>
              <strong>Cara penggunaan:</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              1. Buka file Excel atau Google Sheets<br />
              2. Pilih data yang ingin di-copy (termasuk header)<br />
              3. Paste di area text di bawah ini
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={handleTriggerFileUpload}
                fullWidth
                sx={{ mb: 2 }}
              >
                Upload File Excel
              </Button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".xlsx,.xls,.csv"
                style={{ display: 'none' }}
              />

              <Divider sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  ATAU
                </Typography>
              </Divider>

              <TextField
                label="Paste data dari Excel di sini"
                multiline
                rows={8}
                value={pasteText}
                onChange={handlePasteTextChange}
                placeholder="Copy data dari Excel (termasuk header) dan paste di sini..."
                fullWidth
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PasteIcon color="action" sx={{ mr: 1 }} />
                      <Typography variant="caption" color="text.secondary">
                        Ctrl+V untuk paste
                      </Typography>
                    </Box>
                  ),
                }}
              />
            </Box>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {hasData && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Data berhasil diimport! {summary.rows} baris × {summary.columns} kolom ditemukan.
              </Alert>
            )}
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <DataPreviewTable 
              data={data} 
              headers={headers} 
              rawData={rawData}
            />
            
            {hasData && (
              <Box sx={{ mt: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Total Baris: <strong>{summary.rows}</strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Total Kolom: <strong>{summary.columns}</strong>
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Data Siap Diimport!
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Data berhasil diparsing dengan struktur yang benar.
            </Typography>
            
            <Paper variant="outlined" sx={{ p: 2, mb: 3, textAlign: 'left' }}>
              <Typography variant="body2">
                <strong>Ringkasan Data:</strong><br />
                • Jumlah baris data: {summary.rows}<br />
                • Jumlah kolom: {summary.columns}<br />
                • Kolom tersedia: {headers.join(', ')}
              </Typography>
            </Paper>

            <Button
              variant="outlined"
              onClick={handleExport}
              startIcon={<ExcelIcon />}
              sx={{ mr: 1 }}
            >
              Export ke Excel
            </Button>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen
      maxWidth="lg"
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center' }}>
        <ExcelIcon sx={{ mr: 1 }} />
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          Import Data dari Excel
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <Box sx={{ width: '100%', px: 3, pt: 2 }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent>
        {getStepContent(activeStep)}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Memproses data...
            </Typography>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
        <Button
          onClick={handleBack}
          disabled={activeStep === 0 || loading}
        >
          Kembali
        </Button>

        <Box>
          <Button onClick={handleClose} sx={{ mr: 1 }}>
            Batal
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={(!hasData && activeStep === 0) || loading}
          >
            {activeStep === steps.length - 1 ? 'Import Data' : 'Lanjut'}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}

export default ExcelPasteModal;